<template>
  <div
    v-if="workshop"
    class="workshop"
    :class="{ 'workshop--current': currentWorkshop }"
  >
    <div class="workshop__wrapper">
      <NuxtImg
        class="workshop__image"
        :src="workshop.photo || '/_nuxt/logoBackground.svg'"
        alt="Numer telefonu"
      />
      <div class="workshop__side-section">
        <h1
          class="workshop__name"
          :title="workshop.name"
        >{{ workshopName }}</h1>

        <div class="workshop__rating">
          <div class="workshop__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              width="16px"
              height="16px"
              class="workshop__rating-star"
              alt="Ocena DobryMechanik.pl"
              :src="getStar(workshop.rating_total || 0, index).toString()"
            />
          </div>
          <span class="workshop__rating-total">{{ Number(`${workshop.rating_total}`).toFixed(1) }}</span>

          <span class="workshop__total-comments">({{ workshop.comments_count }} {{ getCommentsTranslatePL(120)
            }})</span>
        </div>

        <div class="workshop__closest-slot">
          <div
            :class="{ 'workshop__closest-slot--open': isOpenByDay(today), 'workshop__closest-slot--closed': !isOpenByDay(today) }"
          >{{
      isOpenByDay(today)
        ? 'Otwarte' : 'Zamknięte' }}</div>
          <div>{{ openingHours(today) }}</div>
        </div>
      </div>
    </div>

    <div class="workshop__bottom-section">
      <div
        v-if="workshop.promoted_service"
        class="workshop__service"
      >
        <div class="workshop__service-name">{{ workshop.promoted_service.name }}</div>
        <div class="workshop__service-price">od {{ Number(workshop.promoted_service.price_from).toFixed(0) }} zł</div>
      </div>

      <div
        class="workshop__opinion"
        v-else-if="workshop.last_comment"
      >
        <NuxtImg
          width="20"
          height="20"
          src="/_nuxt/account.svg"
        />
        <div>
          <div class="workshop__opinion-author">
            {{ workshop.last_comment.name }} {{ workshop.last_comment.car }}
          </div>
          <div class="workshop__opinion-description"> {{ opinionDescription(workshop.last_comment.description) }}
            <span v-if="workshop.last_comment.description.length > shortDescriptionLength">Rozwiń</span>
          </div>
        </div>
      </div>

      <DmButton
        v-if="!!workshop.promoted_service || (!workshop.promoted_service && !workshop.last_comment) "
        tertiary
        class="w-100"
        title="Zobacz więcej"
        has-icon="right"
        icon="i-arrow-right"
      />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { getCommentsTranslatePL } from '@/helpers/helpers';
import { getStar } from '@/helpers/get-star';
import { DmButton } from '@dobrymechanik/vue-ui-v2';
import { type IWorkshopExtended } from '@/pages/mapa/index.vue';
import { dayNumberToString, getStringDayShortcut } from '@/helpers/dayjs';
import { type TDay } from '@/helpers/dayjs';
import dayjs from 'dayjs';

interface IProps {
  workshop: IWorkshopExtended;
  currentWorkshop?: boolean;
}

const props = defineProps<IProps>();

// reactive
const openAtNight: Ref<boolean> = ref(false);

// data
const shortDescriptionLength: number = 92;
const shortWorkshopLength: number = 18;

// methods
const opinionDescription = (opinion: string) => {
  return opinion.length > shortDescriptionLength
    ? opinion.substring(0, shortDescriptionLength) + '...'
    : opinion;
};

// computed 
const workshopName = computed(() => {
  const shortNameLength: number = shortWorkshopLength;
  return props.workshop.name.length > shortNameLength
    ? props.workshop.name.substring(0, shortNameLength) + '...'
    : props.workshop.name;
});

const todayAsDayNumber = computed(() => {
  return dayjs().get('day');
});

const today = computed(() => {
  return dayNumberToString(todayAsDayNumber.value);
});

const isOpenByDay = (day: TDay) => {
  const openHours = props.workshop.open_hours?.[`${day}`];

  if (!openHours?.open) return false;

  const openFrom = openHours.from;
  const openTo = openHours.to;

  const openFromSplitted = openFrom.split(':');
  const openToSplitted = openTo.split(':');

  const current = dayjs().unix();
  const openFromStr = dayjs().set('hour', openFromSplitted[0]).set('minute', openFromSplitted[1]);
  const openToStr = dayjs().set('hour', openToSplitted[0]).set('minute', openToSplitted[1]);

  const openFromStrUnix = openFromStr.unix();
  const openToStrUnix = openToStr.unix();

  const nightOpen = openFromStrUnix > openToStrUnix && openFromStrUnix < current;

  if (nightOpen) {
    openAtNight.value = true;
  }

  return (openFromStrUnix < current && current < openToStrUnix) || nightOpen;
};

const openingHours = (day: TDay) => {
  if (isOpenByDay(day)) {
    const openHours = props.workshop.open_hours?.[`${day}`];
    const openTo = openHours.to;
    const openToSplitted = openTo.split(':');

    let dayToShow = todayAsDayNumber.value;

    if (openAtNight.value) {
      if (dayToShow === 6) {
        dayToShow = 0;
      } else {
        dayToShow += 1;
      }
    }

    return `•   ${getStringDayShortcut(dayToShow)} do ${openToSplitted[0]}:${openToSplitted[1]}`;
  } else {
    const days: TDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const openingHoursArray = days.map((dayName, dayIndex) => {

      return {
        ...props.workshop.open_hours?.[`${dayName}`],
        dayName,
        dayIndex
      };
    });

    const openingHoursArrayOnlyNextDays = openingHoursArray.filter(day => {
      return day.dayIndex > todayAsDayNumber.value;
    });

    let nextOpenDay = openingHoursArrayOnlyNextDays.find(day => {
      return !!day.open;
    });

    const currentDay = openingHoursArray.find(openingHours => {
      return openingHours.open && openingHours.dayIndex === todayAsDayNumber.value;
    });

    if (currentDay) {
      const oFrom = currentDay.from;
      const oFromSplitted = oFrom.split(':');

      const current = dayjs().unix();
      const openFromStr = dayjs().set('hour', oFromSplitted[0]).set('minute', oFromSplitted[1]).unix();

      let dayToShow = currentDay.dayIndex;

      if (openAtNight.value) {
        if (dayToShow === 6) {
          dayToShow = 0;
        } else {
          dayToShow += 1;
        }
      }

      if (current < openFromStr) {
        return `•   ${getStringDayShortcut(dayToShow)} od ${oFromSplitted[0]}:${oFromSplitted[1]}`;
      }
    }

    if (!nextOpenDay) {
      nextOpenDay = openingHoursArray.find(day => {
        return !!day.open;
      });
    }

    if (nextOpenDay) {
      const oFrom = nextOpenDay.from;
      const oFromSplitted = oFrom.split(':');
      return `•   ${getStringDayShortcut(nextOpenDay.dayIndex)} od ${oFromSplitted[0]}:${oFromSplitted[1]}`;
    }

    return '';
  }

};

</script>

<style
  scoped
  lang="scss"
>
.workshop {
  border-radius: 10px;
  border: 1px solid $blue-5;
  background-color: $white;
  width: 100%;
  margin: 0 8px;
  cursor: pointer;
  height: 208px;
  display: flex;
  flex-direction: column;

  &:hover {
    border: 1px solid $blue-4;
    box-shadow: 2px 4px 20px 0px rgba(209, 216, 229, 0.40);
  }

  &__link {
    text-decoration: none;
  }

  &--current {
    border-color: $blue-3;
  }

  @media (min-width: $breakpoint) {
    margin: 0;
    height: auto;
  }

  &__wrapper {
    display: flex;
    gap: 10px;

    @media (min-width: $breakpoint) {
      gap: 12px;
    }
  }

  &__side-section {
    display: flex;
    flex-direction: column;
    gap: 2px;
    justify-content: center;

    @media (min-width: $breakpoint) {
      padding: 14px;
    }

    @media (min-width: 1280px) {
      gap: 5px;
    }
  }

  &__name {
    font-weight: 500;
    margin: 0;
    text-align: left;
    font-size: 16px;
    line-height: 22px;
    cursor: pointer;

    @media (min-width: $breakpoint) {
      font-size: 20px;
      line-height: 28px;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__image {
    border-top-left-radius: 10px;
    width: 117px;
    height: 84px;
    object-fit: cover;


    @media (min-width: $breakpoint) {
      display: none;
    }

    @media (min-width: 1280px) {
      width: 178px;
      height: 112px;
      display: block;
    }
  }

  &__rating {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0 6px;
  }

  &__rating-total {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;

    @media (min-width: $breakpoint) {
      font-size: 16px;
    }
  }

  &__total-comments {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: $blue-1;

    @media (min-width: $breakpoint) {
      font-size: 16px;
    }
  }

  &__rating-star {
    &:not(:last-child) {
      margin-right: 2px;
    }
  }

  &__closest-slot {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0 5px;

    @media (min-width: $breakpoint) {
      font-size: 16px;
      line-height: 22px;
    }

    &--open {
      color: $green-2;
    }

    &--closed {
      color: $red;
    }
  }

  &__opinion {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    line-height: 22px;
    height: 100%;

    @media (min-width: $breakpoint) {
      font-size: 16px;
    }
  }

  &__opinion-author {
    color: $grey-2;
    text-align: left;
  }

  &__opinion-description {
    text-align: left;
    word-break: break-all;

    span {
      color: $blue-1;
    }
  }

  &__bottom-section {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px 0;
    background-color: $blue-6;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    height: 100%;
    justify-content: flex-end;

    @media (min-width: $breakpoint) {
      padding: 14px;
    }
  }

  &__service {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    line-height: 22px;
    gap: 0 10px;
    height: 100%;

    @media (min-width: $breakpoint) {
      display: none;
    }

    @media (min-width: 1280px) {
      display: flex;
      font-size: 16px;
    }
  }

  &__service-name {
    text-transform: capitalize;
    text-align: left;
  }

  &__service-price {
    min-width: 100px;
    font-weight: 500;
    text-align: right;
  }
}
</style>